import React, { useState, useEffect } from 'react';
import Login from './Login';
import AppSelector from './AppSelector';
import AnalyticsDashboard from './AnalyticsDashboard';
import SalesInvoicingModule from './components/SalesInvoicingModule';
import UserManagement from './components/UserManagement';
import CRMModule from './components/CRMModule';
import InventoryManagement from './components/InventoryManagement';
import { supabase } from './lib/supabase';
import { checkBackendHealth } from './lib/api';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentApp, setCurrentApp] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [backendWaking, setBackendWaking] = useState(false);

  useEffect(() => {
    checkSession();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();
            
          setCurrentUser({ ...session.user, profile });
          setIsAuthenticated(true);
          
          // Wake up backend server after successful login
          wakeUpBackend();
        } else if (event === 'SIGNED_OUT') {
          setIsAuthenticated(false);
          setCurrentUser(null);
          setCurrentApp(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const checkSession = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();
          
        setCurrentUser({ ...session.user, profile });
        setIsAuthenticated(true);
        
        // Wake up backend if user has existing session
        wakeUpBackend();
      }
    } catch (error) {
      console.error('Session error:', error);
    } finally {
      setLoading(false);
    }
  };

  const wakeUpBackend = async () => {
    setBackendWaking(true);
    console.log('🔄 Waking up backend server...');
    
    try {
      const result = await checkBackendHealth();
      if (result.success) {
        console.log('✅ Backend is awake and ready!');
      } else {
        console.log('⏳ Backend is waking up... (may take 30-60 seconds)');
        // Retry after delay for Render cold start
        setTimeout(async () => {
          const retryResult = await checkBackendHealth();
          if (retryResult.success) {
            console.log('✅ Backend is now awake!');
          } else {
            console.warn('⚠️ Backend may still be starting up');
          }
        }, 10000); // Retry after 10 seconds
      }
    } catch (error) {
      console.error('Backend wake-up error:', error);
    } finally {
      setBackendWaking(false);
    }
  };

  const handleLogin = (user) => {
    setIsAuthenticated(true);
    setCurrentUser(user);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} darkMode={darkMode} setDarkMode={setDarkMode} />;
  }

  if (!currentApp) {
    return (
      <AppSelector
        user={currentUser}
        onLogout={handleLogout}
        onSelectApp={setCurrentApp}
        darkMode={darkMode}
        setDarkMode={setDarkMode}
      />
    );
  }

  switch (currentApp) {
    case 'dashboard':
      return <AnalyticsDashboard darkMode={darkMode} setDarkMode={setDarkMode} onBack={() => setCurrentApp(null)} />;
    case 'sales-invoicing':
      return <SalesInvoicingModule darkMode={darkMode} setDarkMode={setDarkMode} onBack={() => setCurrentApp(null)} />;
    case 'user-management':
      return <UserManagement darkMode={darkMode} onBack={() => setCurrentApp(null)} />;
    case 'crm':
      return <CRMModule darkMode={darkMode} setDarkMode={setDarkMode} onBack={() => setCurrentApp(null)} />;
    case 'inventory':
      return <InventoryManagement darkMode={darkMode} setDarkMode={setDarkMode} onBack={() => setCurrentApp(null)} />;
    default:
      return <AppSelector user={currentUser} onLogout={handleLogout} onSelectApp={setCurrentApp} darkMode={darkMode} setDarkMode={setDarkMode} />;
  }
}

export default App;
